import {
  CascaderField,
  DateRangePickerField,
  Form,
  NumericFormatField,
  type OperationType,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import {
  POLICY_CODE_LENGTH,
  getCodeMaxValue,
} from '@/features/master-table/utils';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { usePolicyOptions } from '../_hooks/policy';
import { PolicyFormSchema, PolicyLevelLabelModifier } from '../_models/policy';

const policyLevelOptions = [1, 2, 3].map((value) => ({
  label: PolicyLevelLabelModifier[value],
  value,
}));

type FormValues = z.infer<typeof PolicyFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const PolicyForm: FC<Props> = ({ type, initialValues, onSubmit }) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    resetField,
    setValue,
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(PolicyFormSchema),
  });
  const [level, code] = watch(['level', 'code']);

  const policyOptions = usePolicyOptions({
    upperLevel: level ? level - 1 : 0,
  });
  const organizationOptions = useOrganizationOptionsRecursive();

  const codeMaxValue = getCodeMaxValue(POLICY_CODE_LENGTH);

  // NOTE: If you just set initialValues, rhf will not know the value, so setValue is set manually.
  useEffect(() => {
    setValue('upperIds', initialValues?.upperIds);
  }, [initialValues, setValue]);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="level"
        label={t('level')}
        control={control}
        options={policyLevelOptions}
        errors={errors}
        disabled={type === 'edit'}
        shouldCoexistOnChange
        onChange={() => {
          clearErrors('upperIds');
          resetField('upperIds', { defaultValue: null as any });

          if (code && Number(code) > codeMaxValue) {
            setError('code', {
              type: 'max',
              message: t('validation.too-big-number', {
                max: codeMaxValue,
              }),
            });
          } else {
            clearErrors('code');
          }
        }}
        required
      />

      <CascaderField
        name="upperIds"
        label={t('upper-policy')}
        control={control}
        options={policyOptions.data}
        errors={errors}
        disabled={!level || level === 1}
        required={(level ?? 0) > 1}
      />

      <NumericFormatField
        name="code"
        label={t('code')}
        control={control}
        errors={errors}
        disableSeparator
        max={codeMaxValue}
        shouldCoexistOnChange
        onChange={(value) => {
          if (value && value <= codeMaxValue && errors.code?.type === 'max') {
            clearErrors('code');
          }
        }}
        required
        disabled={!level}
      />
      <TextInputField
        name="name"
        autocompleteKey="policy.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />

      <CascaderField
        name="organizationIds"
        label={t('organization-in-charge')}
        control={control}
        options={organizationOptions.data}
        errors={errors}
        enableIntermediate
      />

      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
